// ─────────────────────────────────────────────────────────────────
// Glass-i — Process Department Assignment drawer (Stage 6B.8A-3K.5).
//
// Opens from the Dept cell's "Rates" icon. Shows a compact table:
//   Department | Work Mode | Default? | Pricing Mode | Override Rate | Active
//
// For the common case (Manual + Machine share the same price), the
// operator simply ticks both departments via the cell's multi-select
// and leaves pricing mode = "Same as Process". This drawer is the
// escape hatch for shops that need per-department overrides.
//
// Behavioural contract:
//   • Same as Process  → Override Rate input is disabled + null.
//   • Department Override → Override Rate input becomes required;
//     `validateDepartmentAssignments` returns errors on blank/negative.
//   • Default flag: at most one row can be marked default; the UI
//     enforces single-default via a radio-style behaviour.
//   • All changes are LOCAL until the operator clicks Apply. Apply
//     pushes the normalised set back to the parent cell, which marks
//     the row dirty and queues the full assignments array into the
//     next bulkSave payload.
//
// Strictly UI — no API calls of its own. The parent owns persistence.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import {
  Modal, Table, Select, InputNumber, Switch, Button, Tag, Space,
  Tooltip, Alert, message
} from 'antd';
import { SaveOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';

import {
  PRICING_MODES, validateDepartmentAssignments
} from './processMasterHelpers';

import './ProcessDepartmentDrawer.css';

const WORK_MODE_TAG = {
  manual:         { color: 'blue',    text: 'Manual' },
  machine:        { color: 'gold',    text: 'Machine' },
  mixed:          { color: 'purple',  text: 'Mixed' },
  outsourced:     { color: 'magenta', text: 'Outsourced' },
  not_applicable: { color: 'default', text: 'N/A' }
};

export default function ProcessDepartmentDrawer({
  open, processName, defaultRate, uomCode,
  assignments, departmentLookup, onClose, onApply
}) {
  // Local working copy. We don't mutate the parent's array until Apply.
  const [working, setWorking] = useState([]);

  useEffect(() => {
    if (open) {
      setWorking((assignments || []).map((a) => ({ ...a })));
    }
  }, [open, assignments]);

  const handleRowChange = (idx, patch) => {
    setWorking((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  // Setting isDefault on one row clears it on every other row so the
  // single-default rule holds without confusing the operator.
  const setDefault = (idx, value) => {
    setWorking((prev) => prev.map((r, i) => ({
      ...r, isDefault: i === idx ? value : (value ? false : r.isDefault)
    })));
  };

  const handlePricingModeChange = (idx, mode) => {
    handleRowChange(idx, {
      pricingMode:  mode,
      // Clear override when flipping back to same_as_process so a
      // stale value doesn't sneak into the next save.
      rateOverride: mode === 'department_override'
                      ? (working[idx].rateOverride ?? null)
                      : null
    });
  };

  const handleApply = () => {
    const { ok, errors } = validateDepartmentAssignments(working);
    if (!ok) {
      message.error(errors[0].message);
      return;
    }
    onApply && onApply(working);
  };

  // Look up display fields from the lookup if the assignment row
  // doesn't already carry them (e.g. just-added rows).
  const displayFor = (a) => {
    if (a.departmentName) return { name: a.departmentName,
                                    code: a.departmentCode,
                                    workMode: a.workMode };
    const hit = (departmentLookup || []).find((d) => d.id === a.departmentId);
    return hit ? { name: hit.departmentName, code: hit.departmentCode,
                   workMode: hit.workMode }
               : { name: '?', code: '', workMode: null };
  };

  const columns = [
    { title: 'Department', key: 'dept', width: 220,
      render: (_v, _r, idx) => {
        const d = displayFor(working[idx]);
        return (
          <div className="pda-dept">
            <span className="pda-dept__name">{d.name}</span>
            {d.code ? <span className="pda-dept__code">{d.code}</span> : null}
          </div>
        );
      }
    },
    { title: 'Work Mode', key: 'wm', width: 110,
      render: (_v, _r, idx) => {
        const d = displayFor(working[idx]);
        const tag = WORK_MODE_TAG[d.workMode] || { color: 'default', text: '—' };
        return <Tag color={tag.color} style={{ margin: 0 }}>{tag.text}</Tag>;
      }
    },
    { title: 'Default?', key: 'def', width: 80, align: 'center',
      render: (_v, _r, idx) => (
        <Switch size="small" checked={working[idx].isDefault === true}
          onChange={(c) => setDefault(idx, c)} />
      )
    },
    { title: 'Pricing Mode', key: 'pm', width: 170,
      render: (_v, _r, idx) => (
        <Select size="small" value={working[idx].pricingMode || 'same_as_process'}
          options={PRICING_MODES} style={{ width: '100%' }}
          onChange={(v) => handlePricingModeChange(idx, v)} />
      )
    },
    { title: 'Override Rate', key: 'or', width: 130, align: 'right',
      render: (_v, _r, idx) => {
        const isOverride = working[idx].pricingMode === 'department_override';
        return (
          <InputNumber size="small" min={0} precision={2}
            disabled={!isOverride}
            placeholder={isOverride ? 'Required' : '—'}
            value={working[idx].rateOverride}
            onChange={(n) => handleRowChange(idx, { rateOverride: n == null ? null : Number(n) })}
            style={{ width: '100%' }} />
        );
      }
    },
    { title: 'Active', key: 'is', width: 70, align: 'center',
      render: (_v, _r, idx) => (
        <Switch size="small" checked={working[idx].isActive !== false}
          onChange={(c) => handleRowChange(idx, { isActive: c })} />
      )
    }
  ];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={`Department Assignments — ${processName || 'Process'}`}
      width={920}
      centered
      destroyOnClose
      maskClosable={false}
      className="erp-process-dept-modal"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleApply}>
            Apply
          </Button>
          <Button icon={<CloseOutlined />} onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {working.length === 0 ? (
        <Alert type="info" showIcon icon={<InfoCircleOutlined />}
          message="No departments yet"
          description="Select one or more departments in the Dept cell first. This drawer is for fine-tuning pricing mode per assigned department."
          style={{ marginBottom: 8 }} />
      ) : (
        <>
          <Alert type="info" showIcon
            style={{ marginBottom: 10, padding: '6px 12px' }}
            message={
              <Space size={8}>
                <span>Base rate: <strong>{Number(defaultRate || 0).toFixed(2)}</strong> {uomCode || ''}</span>
                <span style={{ color: '#94a3b8' }}>·</span>
                <span>Same as Process uses the parent rate. Department Override needs a non-negative rate.</span>
              </Space>
            } />
          <Table className="pda-grid" size="small"
            rowKey={(r, i) => r.departmentId || `row-${i}`}
            dataSource={working}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }} />
          <div style={{ marginTop: 10, color: '#64748b', fontSize: 11.5 }}>
            <Tooltip title="Set exactly one department as the default — used by reports that aggregate by primary department.">
              <span>Only one default department is allowed.</span>
            </Tooltip>
          </div>
        </>
      )}
    </Modal>
  );
}
